// @ts-check
"use strict"

import * as Sentry from "@sentry/react";
import { AsyncTaskQueue, exponentialBackoff } from "@minohealth/common/utils/async"
import { Duration } from "@minohealth/common/utils/date"
import axios from "axios"

export class DicomImageUploader {
  static UploadQueue = new AsyncTaskQueue({ concurrency: 5 })

  static MAX_UPLOADS_DELAY = 30 * Duration.MILLISECONDS_PER_SECOND
  static MAX_UPLOADS_RETRIES = 3

  static Events = /** @type {const} */({
    RESET: "reset",
    UPLOAD_START: "upload-start",
    UPLOAD_PROGRESS: "upload-progress",
    UPLOAD_SUCCESS: "upload-success",
    UPLOAD_ERROR: "upload-error",
    UPLOAD_ABORTED: "upload-aborted",
  })

  static Status = /** @type {const} */({
    IDLE: "idle",
    UPLOADING: "uploading",
    SUCCESS: "success",
    ERROR: "error",
  })

  /** @type {EventTarget} */
  #dispatcher
  /** @type {DicomImageUploaderStatus} */
  #status

  constructor() {
    this.#dispatcher = new EventTarget()
    this.#status = DicomImageUploader.Status.IDLE
  }

  get status() {
    return this.#status
  }

  /**
   * @template {DicomImageUploaderEvents} T
   * @param {T} type
   * @param {(event: DicomImageUploaderEventMap[T]) => any} [callback]
   * @param {(boolean|AddEventListenerOptions)} [options]
   */
  addEventListener(type, callback, options) {
    this.#dispatcher.addEventListener(type, callback, options)
  }

  /**
   * @template {DicomImageUploaderEvents} T
   * @param {T} type
   * @param {(event: DicomImageUploaderEventMap[T]) => any} [callback]
   * @param {(boolean|AddEventListenerOptions)} [options]
   */
  removeEventListener(type, callback, options) {
    this.#dispatcher.removeEventListener(type, callback, options)
  }

  reset() {
    this.#status = DicomImageUploader.Status.IDLE
    this.#dispatcher.dispatchEvent(
      new CustomEvent(DicomImageUploader.Events.RESET, {
        detail: { status: this.#status }
      })
    )
  }

  /**
   * @param {File} dicomFile
   * @param {string} userToken
   * @param {boolean} advancedMode
   * @param {Object} [options]
   * @param {AbortSignal} [options.signal]
   */
  async upload(dicomFile, userToken, advancedMode, { signal } = {}) {
    try {
      this.#status = DicomImageUploader.Status.UPLOADING
      this.#dispatcher.dispatchEvent(
        new CustomEvent(DicomImageUploader.Events.UPLOAD_START, {
          detail: { status: this.#status }
        })
      )

      const uploadTask = () => (
        uploadDicom(dicomFile, userToken, advancedMode, {
          signal,
          onUploadProgress: (progressEvent) => {
            const progress = (() => {
              if (progressEvent.total == null || progressEvent.total === 0) return 0;
              return (progressEvent.loaded / progressEvent.total) * 100;
            })();

            this.#dispatcher.dispatchEvent(
              new CustomEvent(DicomImageUploader.Events.UPLOAD_PROGRESS, {
                detail: { progress, status: this.#status }
              })
            );
          },
        })
      )

      const result = await exponentialBackoff(() => (
        DicomImageUploader.UploadQueue.run(uploadTask)
      ), {
        maxRetries: DicomImageUploader.MAX_UPLOADS_RETRIES,
        maxBackoffDelay: DicomImageUploader.MAX_UPLOADS_DELAY,
        stopOnAbort: true,
      })

      this.#status = DicomImageUploader.Status.SUCCESS
      this.#dispatcher.dispatchEvent(
        new CustomEvent(DicomImageUploader.Events.UPLOAD_SUCCESS, {
          detail: { result, status: this.#status }
        })
      )

      return result
    } catch (error) {
      this.#status = DicomImageUploader.Status.ERROR
      if (error.name === "AbortError") {
        this.#dispatcher.dispatchEvent(
          new CustomEvent(DicomImageUploader.Events.UPLOAD_ABORTED, {
            detail: { error, status: this.#status }
          })
        )
      } else {
        this.#dispatcher.dispatchEvent(
          new CustomEvent(DicomImageUploader.Events.UPLOAD_ERROR, {
            detail: { error, status: this.#status }
          })
        )
      }
      throw error
    }
  }
}

/**
 * 3-step upload:
 *   1. GET /pacs/presigned-upload-url  → { uploadUrl, s3Key }
 *   2. PUT file directly to S3 via uploadUrl (reports upload progress)
 *   3. POST /pacs/register-upload { s3Key, advancedMode } → { pacsId, missingFields, extractedFields }
 *
 * @param {File} file
 * @param {string} userToken
 * @param {boolean} advancedMode
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {import("axios").AxiosRequestConfig["onUploadProgress"]} [options.onUploadProgress]
 * @returns {Promise<{ pacsId: string, missingFields: string[], extractedFields: object }>}
 */
async function uploadDicom(file, userToken, advancedMode, { signal, onUploadProgress } = {}) {
  const baseURL = import.meta.env.VITE_SOCKET_URL
  const headers = { Authorization: `Bearer ${userToken}` }

  try {
    // Step 1: get presigned PUT URL
    const { data: presignedData } = await axios.get("pacs/presigned-upload-url", {
      baseURL,
      headers,
      params: { filename: file.name },
      signal,
    })
    const { uploadUrl, s3Key } = presignedData

    // Step 2: upload directly to S3 (no auth header — presigned URL carries credentials)
    await axios.put(uploadUrl, file, {
      headers: { "Content-Type": "application/octet-stream" },
      signal,
      onUploadProgress,
    })

    // Step 3: register with backend
    const { data: registerData } = await axios.post("pacs/register-upload", {
      s3Key,
      advancedMode,
    }, {
      baseURL,
      headers: { ...headers, "Content-Type": "application/json" },
      signal,
    })

    return registerData // { pacsId, missingFields, extractedFields }
  } catch (error) {
    // A refusal, such as too few credits, carries its reason for the user. Another try gives the same answer.
    const reason = error?.response?.status === 403 ? error.response.data?.error : null
    if (typeof reason === "string") {
      const refusal = new Error(reason, { cause: error })
      refusal.retryable = false
      throw refusal
    }

    Sentry.captureException(error, {
      extra: {
        context: "pacs-system utils - uploadDicom",
        fileName: file?.name,
        fileSize: file?.size,
        hasUserToken: !!userToken,
        aborted: signal?.aborted,
      }
    });
    throw new Error("Failed to upload dicom", { cause: error })
  }
}

/** @typedef {typeof DicomImageUploader.Events[keyof typeof DicomImageUploader.Events]} DicomImageUploaderEvents */
/** @typedef {typeof DicomImageUploader.Status[keyof typeof DicomImageUploader.Status]} DicomImageUploaderStatus */
/** @typedef {Awaited<ReturnType<DicomImageUploader['upload']>>} DicomImageUploaderResult */

/**
 * @typedef {
    {
      [K in DicomImageUploaderEvents]:
        K extends "upload-progress"
          ? CustomEvent<{progress: number, status: DicomImageUploaderStatus}> :
        K extends "upload-success"
          ? CustomEvent<{result: DicomImageUploaderResult, status: DicomImageUploaderStatus}> :
        K extends "upload-error" | "upload-aborted"
          ? CustomEvent<{error: Error, status: DicomImageUploaderStatus}>
        : CustomEvent<{status: DicomImageUploaderStatus}>
    }
  } DicomImageUploaderEventMap
 */
